import { WORLD } from "@clubap/shared";
import { Container, Graphics } from "pixi.js";
import { HORIZON, building, outdoor, tree } from "./common.js";

/**
 * El patio de contenedores: las cinco puertas de los clubes.
 *
 * Está copiado de los contenedores de verdad de la ESPOL — cajas de carga recicladas,
 * pintadas de colores y puestas en fila sobre una losa. Acá cada una es la puerta de un
 * club, y por eso este archivo NO las dibuja: los cinco cajones son elementos del
 * catálogo (`kind: "club"`), los pinta `RoomStage` con el color de su club y hay que
 * poder tocarlos. Lo que se pinta acá es dónde están parados.
 *
 * La losa es lo único que este sitio necesita para leerse: un patio de carga es asfalto
 * con juntas, y sobre él cualquier cosa parece puesta ahí a propósito.
 */
export function contenedores(): Container {
  const scene = outdoor({ ground: 0x9a9a96, hedge: 0x4c8f52 });

  // Los bloques del campus asomando por detrás: sitúan el patio dentro de la ESPOL.
  scene.addChild(building(40, 92, 230, 130, 0xe3dccd));
  scene.addChild(building(700, 74, 220, 148, 0xd9d2c4));
  scene.addChild(tree(330, HORIZON + 4, 0.85));
  scene.addChild(tree(620, HORIZON + 4, 0.8));

  /*
   * Una fila de contenedores apilados AL FONDO, a media altura del horizonte.
   *
   * No son puertas y no se tocan: son el decorado que hace que cinco cajones sueltos en
   * el suelo se lean como un patio de carga y no como cinco cajas en un parque. Por eso
   * van apagados y pequeños — lo que se toca tiene que destacar sobre ellos.
   */
  const fondo = new Graphics();
  const apilados: [number, number, number][] = [
    [96, 176, 0x7d6a52],
    [176, 176, 0x5f6f7c],
    [256, 176, 0x7a5350],
    [430, 182, 0x60705a],
    [510, 182, 0x6b6470],
  ];

  for (const [x, y, color] of apilados) {
    fondo.rect(x, y, 76, 44).fill({ color, alpha: 0.7 }).stroke({ width: 2, color: 0x3d3a33 });
    for (let i = 1; i < 4; i++) {
      fondo.moveTo(x + (76 / 4) * i, y + 4).lineTo(x + (76 / 4) * i, y + 40);
    }
  }
  fondo.stroke({ width: 1, color: 0x3d3a33, alpha: 0.4 });
  scene.addChild(fondo);

  /*
   * La losa: juntas rectas, no en perspectiva.
   *
   * Los interiores usan punto de fuga (ver `indoor`), pero un patio pavimentado se lee
   * mejor con la cuadrícula recta — las juntas de una losa de verdad son paralelas, y
   * forzarlas a converger acá haría que el suelo pareciera inclinarse.
   */
  const losa = new Graphics();
  for (let x = 0; x <= WORLD.width; x += 96) {
    losa.moveTo(x, HORIZON).lineTo(x, WORLD.height);
  }
  for (let y = HORIZON + 48; y < WORLD.height; y += 48) {
    losa.moveTo(0, y).lineTo(WORLD.width, y);
  }
  losa.stroke({ width: 2, color: 0x000000, alpha: 0.1 });

  // La franja pintada del carril de maniobra, que es lo que delata que esto es un patio.
  losa.rect(0, HORIZON + 12, WORLD.width, 6).fill({ color: 0xe8d36a, alpha: 0.55 });
  scene.addChild(losa);

  return scene;
}
